import { useCallback, useContext } from "react";
import { UserContext } from "../pages/UserContext";
import { generateLogoutUrl } from "../utlities/auth.utility";
import { ACTIVITY_STORAGE_KEY } from "../utlities/core.constants";
import { clearAllEditorDrafts } from "../components/acr-editor/stores/EvaluationContext";

/** Cap on how long sign-out waits for the impersonation end call so a dead network can't block it. */
const END_IMPERSONATION_TIMEOUT_MS = 3000;

/**
 * Ends the user's session: clears local app state, then redirects the browser
 * to Login.gov's RP-Initiated Logout endpoint so the Login.gov SSO cookie is
 * actually cleared (simply dropping our own JWT leaves that cookie intact,
 * letting the user get silently re-authenticated on the next login attempt).
 *
 * Login.gov shows its own confirmation page before ending the session; on
 * success it redirects back to REACT_APP_OAUTH_POST_LOGOUT_REDIRECT_URI, which
 * must be the app's Home page and pre-registered with Login.gov for this
 * client.
 */
export default function useLogout() {
  const { setJwt, endImpersonation } = useContext(UserContext);

  const logout = useCallback(async () => {
    try {
      localStorage.removeItem(ACTIVITY_STORAGE_KEY);
      // Don't leave in-progress ACR drafts on a shared device after sign-out.
      clearAllEditorDrafts();
    } catch { /* localStorage may be unavailable */ }
    // End any active impersonation session server-side *before* dropping the token and navigating
    // away, so it can't be resurrected on the next login. Awaited (the call still carries a valid
    // auth header at this point), but capped so a slow/dead network can never block sign-out.
    try {
      await Promise.race([
        Promise.resolve(endImpersonation?.()),
        new Promise((resolve) => { setTimeout(resolve, END_IMPERSONATION_TIMEOUT_MS); }),
      ]);
    } catch { /* best effort */ }
    setJwt(null);
    window.location.href = generateLogoutUrl();
  }, [setJwt, endImpersonation]);

  return { logout };
}
